import React, { type HTMLAttributes } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import Immutable, { fromJS } from 'immutable';

import { Container, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Paragraph } from '@uncinc/react-drupal-core';

import {
  WysiwygField,
  Breadcrumbs,
  ButtonWrapper,
  Share,
  DynamicMediaItem,
} from '../../../components';
import {
  SCREEN_TABLET,
  APP_OPTIONS,
} from '../../../config/constants';
import type { NodeStory as Type } from '../../../types/uncinc_profile/nodes';

import './style.scss';


interface StoryProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const NodeStory: React.FC<StoryProps> = ({
  entity,
  className,
}) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isLarge = width > SCREEN_TABLET;
  const classes = c(className, 'NodeStory');
  const story = entity.toJS();
  const body = story.body[0]?.processed;
  const image = story.field_media_item[0];
  const video = story.field_video[0];
  const paragraphs = story.field_paragraphs;
  const project = story.field_project[0];
  const language = project?.entity?.langcode[0]?.value || 'nl';
  const projectPath = project?.entity?.paths[language];
  const quote = story.field_quote[0]?.value;

  const breadcrumbs = [
    {
      title: t('home'),
      url: '/',
    },
    {
      title: t('maatjes.general.stories'),
      url: APP_OPTIONS?.parameters.overviewUrls.maatjes_story,
    },
  ];

  return (
    // eslint-disable-next-line react/no-unknown-property
    <article typeof="schema:WebPage" className={classes}>
      <div className="NodeStory">
        <Container>
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <Breadcrumbs
                includeCurrent
                hideHome={false}
                // @ts-expect-error
                customCrumbs={fromJS(breadcrumbs)}
              />
              {video && (
                <div className="NodeStory__video">
                  <DynamicMediaItem
                    media={video}
                    posterImage={image.styles['16_9']}
                  />
                </div>
              )}
              {quote && (<h1 className="NodeStory__quote">{quote}</h1>)}
              {body && (
                <WysiwygField
                  property="schema:text"
                  className="NodeStory__body"
                  html={body}
                />
              )}
              {(image && !video) && (
                <div className="NodeStory__image">
                  <DynamicMediaItem
                    media={image}
                    imageAspectRatio="2_1"
                  />
                </div>
              )}
            </div>
          </div>
        </Container>
        {paragraphs.length > 0 && (
          <div className="Paragraphs NodeStory__paragraphs">
            {paragraphs.map((paragraph) => {
              const targetId = paragraph.target_id;

              return (
                <Paragraph
                  key={`paragraph:${targetId}`}
                  id={targetId}
                  entity={Immutable.fromJS(paragraph.entity)}
                />
              );
            })}
          </div>
        )}

        <Container>
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <div className="NodeStory__links">
                <div className="NodeStory__links__project">
                  {projectPath && (
                    <>
                      <p className="NodeStory__links__project__label">{t('story.button_label')}</p>
                      <ButtonWrapper className="NodeStory__links__project__button" href={projectPath}>{t('story.button_text')}</ButtonWrapper>
                    </>
                  )}
                </div>
                <Share title={t('share')} />
              </div>
            </div>
          </div>
        </Container>
      </div>
    </article>
  );
};

export default NodeStory;
